// External Imports
import { useState, useRef, useEffect, useContext } from "react";

// Internal Imports
import LoadingSpinner from "../UI/LoadingSpinner";
import { requestAdminAPI } from "../../api/posh-api";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import { MdClose } from "react-icons/md";
import DetailPair from "../UI/DetailPair";
import { constructDateObject, isoDateToString } from "../../util/format-date";
import Switch from "../UI/Switch";
import { differenceInDays, format, getDaysInMonth } from "date-fns";
import { DataContext } from "../../context/data-context";

/** ========================================================================
 * * ChangeEndDate Component
 * Renders a form, which allows the admin to extend a booking.
 * ========================================================================== */

export default function ChangeEndDate({ booking, open, setOpen, onComplete }) {
  /* 
  * Declare refs 
  Refs are used to updated values in the form fields.
  */
  const bookingFeeRef = useRef();
  const taxesRef = useRef();
  const totalFeeRef = useRef();

  // Initialize States
  const [loading, setLoading] = useState(false);

  /* ======================= CALCULATE TOTAL FEE ======================= */
  const calculateTotalFee = () => {
    const totalFee =
      Number(bookingFeeRef.current.value) + Number(taxesRef.current.value);
    totalFeeRef.current.value = "$" + totalFee.toFixed(2);
  };

  /* ======================= FETCH PRICE ======================= */
  const fetchPrice = async () => {
    const payload = {
      vehicleId: booking.vehicleId,
      customerId: booking.customerId,
      startDate: isoDateToString(booking.endDate, "yyyy-MM-dd"),
      startTime: isoDateToString(booking.endDate, "HH:mm"),
      endDate: document.getElementById("endDate").value,
      endTime: document.getElementById("endTime").value,
    };
    let flag = true;
    for (const key of Object.keys(payload)) {
      if (payload[key] === "") {
        flag = false;
        break;
      }
    }
    if (!flag) return;
    try {
      setLoading(true);
      // Send API request
      const price = await requestAdminAPI("fetch-price", payload);

      // Update Inputs
      document.getElementById("bookingFee").value = price.subtotal;
      document.getElementById("taxes").value = price.taxes;
      document.getElementById("milesIncluded").value = price.milesIncluded;
      calculateTotalFee();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= SUBMIT HANDLER ======================= */
  const submitHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        bookingId: booking.id,
        dropOffLocation: document.getElementById("dropOffLocation").value,
        endDate: document.getElementById("endDate").value,
        endTime: document.getElementById("endTime").value,
        milesIncluded: parseInt(
          document.getElementById("milesIncluded").value || 0
        ),
        mileageRate: parseFloat(
          document.getElementById("mileageRate").value || 0
        ),
        bookingFee: parseFloat(
          document.getElementById("bookingFee").value || 0
        ),
        taxes: parseFloat(document.getElementById("taxes").value || 0),
        daysUntilDue: parseInt(
          document.getElementById("daysUntilDue").value || 0
        ),
      };
      await requestAdminAPI("extend-booking", payload);
      setOpen(false);
      onComplete && onComplete();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
     fetchPrice();
  }, []);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal gap-6 lg:w-[45vw]">
        <Heading title="Change End Date" isBackArrow={false}>
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <div className="info lg:grid-cols-4">
          <DetailPair
            label="End Date"
            edit={true}
            inputOptions={{
              type: "date",
              id: "endDate",
              onChange: fetchPrice,
            }}
          />
          <DetailPair
            label="End Time"
            edit={true}
            inputOptions={{
              type: "time",
              id: "endTime",
              onChange: fetchPrice,
            }}
          />
          <DetailPair
            label="Miles Included"
            edit={true}
            inputOptions={{
              type: "number",
              id: "milesIncluded",
            }}
          />
          <DetailPair
            label="Mileage Rate"
            edit={true}
            inputOptions={{
              type: "number",
              id: "mileageRate",
              defaultValue: booking.mileageRate,
            }}
          />
          <DetailPair
            label="Booking Fee"
            edit={true}
            inputOptions={{
              type: "number",
              id: "bookingFee",
              onChange: calculateTotalFee,
              ref: bookingFeeRef,
            }}
          />
          <DetailPair
            label="Taxes"
            edit={true}
            inputOptions={{
              type: "number",
              id: "taxes",
              onChange: calculateTotalFee,
              ref: taxesRef,
            }}
          />
          <DetailPair
            label="Days Until Due"
            edit={true}
            inputOptions={{
              type: "number",
              id: "daysUntilDue",
              defaultValue: "3",
            }}
          />
          <DetailPair
            label="Total Fee"
            edit={true}
            inputOptions={{
              type: "text",
              id: "totalFee",
              disabled: true,
              ref: totalFeeRef,
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              label="Drop Off Location"
              edit={true}
              inputOptions={{
                type: "text",
                id: "dropOffLocation",
                defaultValue: booking?.dropOffLocation,
              }}
            />
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-2 gap-4 lg:col-span-4">
              <div onClick={() => setOpen(false)} className="btn-secondary">
                Cancel
              </div>
              <div
                onClick={submitHandler}
                className="btn-primary"
                disabled={loading}
              >
                Submit
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
